// src/admin/pages/orders/invoiceService.ts
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Order } from '../../../enums/order';
import { CompanyProfile } from '../company/companyService';
import { getCurrencySymbol, formatNumber } from '../../../util/Formatcurrency';

export class InvoiceService {
  /**
   * Load image and convert to base64
   */
  private static async loadImageAsBase64(imagePath: string): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL('image/png');
            resolve(base64);
          } else {
            resolve(null);
          }
        } catch (error) {
          console.warn('Could not convert image to base64:', error);
          resolve(null);
        }
      };

      img.onerror = (error) => {
        console.warn('Could not load image:', imagePath, error);
        resolve(null);
      };

      // Set source after setting up handlers
      img.src = imagePath;

      // Timeout after 5 seconds
      setTimeout(() => {
        resolve(null);
      }, 5000);
    });
  }

  /**
   * Fetch image via fetch API (works better for Firebase Storage URLs with CORS)
   */
  private static async fetchImageAsBase64(imageUrl: string): Promise<string | null> {
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        cache: 'no-cache',
      });

      if (!response.ok) {
        console.warn('Failed to fetch image:', response.status, response.statusText);
        return null;
      }

      const blob = await response.blob();

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          console.warn('Failed to read blob as base64');
          resolve(null);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Error fetching image:', error);
      return null;
    }
  }

  /**
   * Normalize image path
   */
  private static normalizeImagePath(path: string): string {
    // If it's already a full URL (http/https), return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // If it starts with 'public/', fix the path
    if (path.startsWith('public/')) {
      const cleanPath = path.replace('public/', '');
      return `${window.location.origin}/${cleanPath}`;
    }

    // If it starts with '/', it's an absolute path
    if (path.startsWith('/')) {
      return `${window.location.origin}${path}`;
    }

    // Otherwise, treat as relative path
    return `${window.location.origin}/${path}`;
  }

  /**
   * Add page number footers to every page after all content is generated
   */
  private static addPageNumberFooters(doc: jsPDF, footerText: string): void {
    const totalPages = doc.getNumberOfPages();
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY    = pageHeight - 20;

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128, 128, 128);

      // Centered footer text
      doc.text(footerText, pageWidth / 2, footerY, { align: 'center' });

      // Correct page number on every page
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, footerY, { align: 'right' });
    }
  }

  /**
   * Generate and download invoice PDF for an order
   */
  static async generateInvoice(order: Order, companyProfile: CompanyProfile | null): Promise<void> {
    try {
      const doc = new jsPDF();
      const pageWidth  = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // ── Currency helpers (shared utility) ──────────────────────────────────
      const currency       = companyProfile?.currency || 'USD';
      const currencySymbol = getCurrencySymbol(currency);

      /**
       * Format a monetary amount for the PDF:
       *   Rs 1,234,567.50
       * Uses the shared formatNumber() so thousands separators are consistent
       * with the rest of the application.
       */
      const formatCurrencyPdf = (amount: number): string =>
        `${currencySymbol} ${formatNumber(amount, 2)}`;

      /**
       * Format a plain quantity (integer) with thousands separators:
       *   1,234
       */
      const formatQtyPdf = (qty: number): string => formatNumber(qty, 0);

      // ==================== HEADER SECTION ====================

      // Company Logo - Try multiple sources
      try {
        const rawLogoSources = [
          companyProfile?.logo,
          '/asset/images/logo/logo.png',
          'asset/images/logo/logo.png',
        ].filter(Boolean) as string[];

        // Normalize all paths
        const logoSources = rawLogoSources.map(path => this.normalizeImagePath(path));

        console.log('Attempting to load logo from sources:', logoSources);

        let logoLoaded = false;

        for (const logoPath of logoSources) {
          if (logoLoaded) break;

          console.log('Trying logo source:', logoPath);

          let base64Logo: string | null = null;

          // Try fetch method first (better for Firebase Storage URLs)
          if (
            logoPath.includes('firebasestorage.googleapis.com') ||
            logoPath.includes('firebase')
          ) {
            console.log('Using fetch method for Firebase URL');
            base64Logo = await this.fetchImageAsBase64(logoPath);
          }

          // If fetch didn't work or not a Firebase URL, try Image method
          if (!base64Logo) {
            console.log('Using Image method');
            base64Logo = await this.loadImageAsBase64(logoPath);
          }

          if (base64Logo) {
            try {
              doc.addImage(base64Logo, 'PNG', 15, yPosition, 30, 30);
              logoLoaded = true;
              console.log('✓ Logo successfully loaded from:', logoPath);
            } catch (error) {
              console.warn('Could not add logo to PDF:', error);
            }
          } else {
            console.warn('Failed to load logo from:', logoPath);
          }
        }

        if (!logoLoaded) {
          console.warn('No logo could be loaded for invoice from any source');
        }
      } catch (error) {
        console.warn('Error in logo loading process:', error);
      }

      // Company Information (right side of logo)
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(
        companyProfile?.companyName || 'Company Name',
        pageWidth - 15,
        yPosition,
        { align: 'right' }
      );

      yPosition += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      if (companyProfile?.address) {
        const addressLines = [
          companyProfile.address.street,
          `${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.postalCode}`,
          companyProfile.address.country,
        ].filter(Boolean);

        addressLines.forEach(line => {
          doc.text(line, pageWidth - 15, yPosition, { align: 'right' });
          yPosition += 5;
        });
      }

      if (companyProfile?.contactInfo?.email) {
        doc.text(`Email: ${companyProfile.contactInfo.email}`, pageWidth - 15, yPosition, { align: 'right' });
        yPosition += 5;
      }

      if (companyProfile?.contactInfo?.phone) {
        doc.text(`Phone: ${companyProfile.contactInfo.phone}`, pageWidth - 15, yPosition, { align: 'right' });
        yPosition += 5;
      }

      if (companyProfile?.contactInfo?.website) {
        doc.text(`Website: ${companyProfile.contactInfo.website}`, pageWidth - 15, yPosition, { align: 'right' });
        yPosition += 5;
      }

      // ── Tax ID removed ─────────────────────────────────────────────────────

      yPosition = Math.max(yPosition, 55); // Ensure we're past the logo

      // ==================== INVOICE TITLE ====================

      yPosition += 10;
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 204); // Blue color
      doc.text('INVOICE', 15, yPosition);

      // Invoice details (right side)
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Invoice #: ${order.orderNumber}`, pageWidth - 15, yPosition - 5, { align: 'right' });
      doc.text(`Date: ${order.orderDate.toLocaleDateString()}`, pageWidth - 15, yPosition + 2, { align: 'right' });

      if (order.estimatedDelivery) {
        doc.text(
          `Expected Delivery Date: ${order.estimatedDelivery.toLocaleDateString()}`,
          pageWidth - 15,
          yPosition + 9,
          { align: 'right' }
        );
      }

      yPosition += 20;

      // ==================== SEPARATOR LINE ====================

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 10;

      // ==================== BILLING INFORMATION ====================

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Bill To:', 15, yPosition);

      yPosition += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Customer name
      doc.setFont('helvetica', 'bold');
      doc.text(order.customerName, 15, yPosition);
      yPosition += 5;
      doc.setFont('helvetica', 'normal');

      // Customer email
      if (order.customerEmail) {
        doc.text(order.customerEmail, 15, yPosition);
        yPosition += 5;
      }

      // Customer phone
      if (order.customerPhone) {
        doc.text(order.customerPhone, 15, yPosition);
        yPosition += 5;
      }

      // ==================== SHIPPING INFORMATION ====================

      const shippingStartY = yPosition - 17; // Align with "Bill To"
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Ship To:', pageWidth / 2 + 10, shippingStartY);

      let shippingY = shippingStartY + 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      if (order.shippingAddress) {
        if (order.shippingAddress.fullName) {
          doc.setFont('helvetica', 'bold');
          doc.text(order.shippingAddress.fullName, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
          doc.setFont('helvetica', 'normal');
        }

        if (order.shippingAddress.addressLine1) {
          doc.text(order.shippingAddress.addressLine1, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
        }

        if (order.shippingAddress.addressLine2) {
          doc.text(order.shippingAddress.addressLine2, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
        }

        const cityLine = [
          order.shippingAddress.city,
          order.shippingAddress.state,
          order.shippingAddress.postalCode,
        ]
          .filter(Boolean)
          .join(', ');

        if (cityLine) {
          doc.text(cityLine, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
        }

        if (order.shippingAddress.country) {
          doc.text(order.shippingAddress.country, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
        }

        if (order.shippingAddress.phoneNumber) {
          doc.text(`Phone: ${order.shippingAddress.phoneNumber}`, pageWidth / 2 + 10, shippingY);
          shippingY += 5;
        }
      }

      yPosition = Math.max(yPosition, shippingY) + 10;

      // ==================== ORDER DETAILS TABLE ====================

      // Prepare table data — quantities and prices are comma-formatted
      const tableData = order.items.map((item, index) => {
        let productName = item.productName;

        if (item.variantSelections && item.variantSelections.length > 0) {
          const variantInfo = item.variantSelections
            .map(v => `${v.variantType}: ${v.variantValue}`)
            .join(', ');
          productName += `\n(${variantInfo})`;
        }

        return [
          (index + 1).toString(),
          productName,
          formatQtyPdf(item.quantity),           // e.g. "1,234"
          formatCurrencyPdf(item.unitPrice),      // e.g. "Rs 1,234.50"
          formatCurrencyPdf(item.totalPrice),     // e.g. "Rs 12,345.00"
        ];
      });

      // Draw items table
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Product', 'Qty', 'Unit Price', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [0, 102, 204],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'center',
        },
        bodyStyles: {
          fontSize: 9,
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 15 }, // Row number
          1: { cellWidth: 80 }, // Product
          2: { cellWidth: 20 }, // Qty
          3: { cellWidth: 30 }, // Unit Price
          4: { cellWidth: 35 }, // Total
        },
        margin: { left: 15, right: 15 },
        // Reserve space at the bottom of every page so footers are never
        // overwritten by table rows
        showHead: 'everyPage',
        didDrawPage: () => {
          // No inline footer here — footers are stamped after all content
          // is generated so we know the true total page count.
        },
      });

      // Get Y position after table
      yPosition = (doc as any).lastAutoTable.finalY + 10;

      // ==================== SUMMARY SECTION ====================

      // If summary would overflow the page, push to a new one
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = 20;
      }

      const summaryX      = pageWidth - 80;
      const summaryLabelX = summaryX - 5;
      const summaryValueX = pageWidth - 15;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      // Subtotal
      doc.text('Subtotal:', summaryLabelX, yPosition, { align: 'right' });
      doc.text(formatCurrencyPdf(order.subtotal), summaryValueX, yPosition, { align: 'right' });
      yPosition += 6;

      // Tax
      if (order.taxAmount > 0) {
        doc.text('Tax:', summaryLabelX, yPosition, { align: 'right' });
        doc.text(formatCurrencyPdf(order.taxAmount), summaryValueX, yPosition, { align: 'right' });
        yPosition += 6;
      }

      // Shipping
      if (order.shippingCost > 0) {
        doc.text('Shipping:', summaryLabelX, yPosition, { align: 'right' });
        doc.text(formatCurrencyPdf(order.shippingCost), summaryValueX, yPosition, { align: 'right' });
        yPosition += 6;
      }

      // Discount
      if (order.discountAmount > 0) {
        doc.text('Discount:', summaryLabelX, yPosition, { align: 'right' });
        doc.text(`-${formatCurrencyPdf(order.discountAmount)}`, summaryValueX, yPosition, { align: 'right' });
        yPosition += 6;
      }

      // Separator line before total
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(summaryLabelX - 45, yPosition, summaryValueX, yPosition);
      yPosition += 7;

      // Total
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', summaryLabelX, yPosition, { align: 'right' });
      doc.text(formatCurrencyPdf(order.totalAmount), summaryValueX, yPosition, { align: 'right' });

      yPosition += 15;

      // ==================== PAYMENT INFORMATION ====================

      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Payment Information:', 15, yPosition);
      yPosition += 6;

      doc.setFont('helvetica', 'normal');
      doc.text(`Payment Status: ${this.getPaymentStatusLabel(order.paymentStatus)}`, 15, yPosition);
      yPosition += 5;
      doc.text(`Payment Method: ${this.getPaymentMethodLabel(order.paymentMethod)}`, 15, yPosition);
      yPosition += 5;
      doc.text(`Order Status: ${this.getOrderStatusLabel(order.status)}`, 15, yPosition);

      if (order.trackingNumber) {
        yPosition += 5;
        doc.text(`Tracking Number: ${order.trackingNumber}`, 15, yPosition);
      }

      yPosition += 15;

      // ==================== NOTES ====================

      if (order.notes) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 15, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(order.notes, pageWidth - 30);
        doc.text(splitNotes, 15, yPosition);
      }

      // ==================== FOOTERS (all pages, correct page count) ====================
      // Stamp footers AFTER all content so getNumberOfPages() is accurate.
      const footerText = companyProfile?.invoiceFooter || 'Thank you for your business!';
      this.addPageNumberFooters(doc, footerText);

      // ==================== SAVE PDF ====================

      const fileName = `Invoice_${order.orderNumber}_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating invoice:', error);
      throw new Error('Failed to generate invoice. Please try again.');
    }
  }

  /**
   * Preview invoice in new window/tab
   */
  static async previewInvoice(order: Order, companyProfile: CompanyProfile | null): Promise<void> {
    try {
      // Generate the PDF (reuse the same logic)
      const doc = new jsPDF();
      // ... (copy the same generation logic from generateInvoice)

      // Instead of save(), use output to open in new window
      const pdfBlob = doc.output('blob');
      const pdfUrl  = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');

      // Clean up the URL after some time
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
    } catch (error) {
      console.error('Error previewing invoice:', error);
      throw new Error('Failed to preview invoice. Please try again.');
    }
  }

  /**
   * Get invoice as blob (for email attachments, etc.)
   */
  static async getInvoiceBlob(order: Order, companyProfile: CompanyProfile | null): Promise<Blob> {
    try {
      // Generate the PDF (reuse the same logic)
      const doc = new jsPDF();
      // ... (copy the same generation logic from generateInvoice)

      return doc.output('blob');
    } catch (error) {
      console.error('Error generating invoice blob:', error);
      throw new Error('Failed to generate invoice. Please try again.');
    }
  }

  // ── Helper methods for labels ─────────────────────────────────────────────
  private static getPaymentStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending:            'Pending',
      paid:               'Paid',
      failed:             'Failed',
      refunded:           'Refunded',
      partially_refunded: 'Partially Refunded',
    };
    return labels[status] || status;
  }

  private static getPaymentMethodLabel(method: string): string {
    const labels: Record<string, string> = {
      credit_card:      'Credit Card',
      debit_card:       'Debit Card',
      paypal:           'PayPal',
      bank_transfer:    'Bank Transfer',
      cash_on_delivery: 'Cash on Delivery',
      crypto:           'Cryptocurrency',
    };
    return labels[method] || method;
  }

  private static getOrderStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending:    'Pending',
      confirmed:  'Confirmed',
      processing: 'Processing',
      shipped:    'Shipped',
      delivered:  'Delivered',
      cancelled:  'Cancelled',
      returned:   'Returned',
    };
    return labels[status] || status;
  }
}